import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useApp } from "@/context/AppContext";
import { ROLES_LABEL } from "@/lib/format";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { SidebarContent } from "@/components/Sidebar";
import { LogOut, Menu, Search, ChevronsUpDown, Building2, CalendarDays } from "lucide-react";
import api from "@/lib/api";
import { fileUrl } from "@/lib/files";
import { useNavigate } from "react-router-dom";

export function Topbar() {
  const { user, logout } = useAuth();
  const { societes, societeId, selectSociete, exercice, selectExercice, annees } = useApp();
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const navigate = useNavigate();

  const doSearch = async (val) => {
    setQ(val);
    if (val.length < 2) return setResults(null);
    try {
      const { data } = await api.get("/search", { params: { q: val } });
      setResults(data);
    } catch (e) {
      setResults(null);
    }
  };

  const initials = (user?.name || "?")
    .split(" ")
    .map((s) => s[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  return (
    <header className="sticky top-0 z-30 h-16 border-b border-border/70 bg-background/85 backdrop-blur-xl">
      <div className="flex h-full items-center gap-3 px-4 sm:px-6">
        {/* Mobile menu */}
        <Sheet>
          <SheetTrigger asChild>
            <Button variant="ghost" size="icon" className="lg:hidden" data-testid="mobile-menu-btn">
              <Menu className="h-5 w-5" />
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="p-0 w-72 bg-card">
            <SidebarContent />
          </SheetContent>
        </Sheet>

        {/* Société selector */}
        {(() => { const sel = societes.find((s) => s.id === societeId); return sel?.logo_id ? <img src={fileUrl(sel.logo_id)} alt="logo" className="h-9 w-9 rounded-md object-contain border bg-white hidden sm:block" data-testid="societe-logo" /> : null; })()}
        <Select value={societeId} onValueChange={selectSociete}>
          <SelectTrigger className="w-[190px] sm:w-[240px] gap-2" data-testid="societe-selector">
            <Building2 className="h-4 w-4 text-primary shrink-0" />
            <SelectValue placeholder="Société" />
          </SelectTrigger>
          <SelectContent className="bg-popover">
            <SelectItem value="all">Toutes les sociétés</SelectItem>
            {societes.map((s) => (
              <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Exercice (année) selector */}
        <Select value={exercice} onValueChange={selectExercice}>
          <SelectTrigger className="w-[130px] gap-2" data-testid="exercice-selector">
            <CalendarDays className="h-4 w-4 text-primary shrink-0" />
            <SelectValue placeholder="Exercice" />
          </SelectTrigger>
          <SelectContent className="bg-popover">
            <SelectItem value="all">Tous exercices</SelectItem>
            {annees.map((y) => (
              <SelectItem key={y} value={y} data-testid={`exercice-${y}`}>Exercice {y}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Search */}
        <div className="relative hidden md:block flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            data-testid="global-search"
            value={q}
            onChange={(e) => doSearch(e.target.value)}
            placeholder="Rechercher facture, fournisseur, dépense…"
            className="pl-9"
          />
          {results && (
            <div className="absolute mt-2 w-full rounded-lg border bg-popover shadow-lg p-2 z-50 max-h-80 overflow-y-auto text-sm">
              {[
                ["Dépenses", results.depenses, (r) => `${r.numero} · ${r.description}`, "/depenses"],
                ["Fournisseurs", results.fournisseurs, (r) => r.raison_sociale, "/fournisseurs"],
                ["Factures", results.factures, (r) => `${r.numero_facture}`, "/factures-fournisseurs"],
              ].map(([label, arr, fmt, to]) =>
                arr?.length ? (
                  <div key={label} className="mb-1">
                    <p className="px-2 py-1 text-[10px] uppercase text-muted-foreground">{label}</p>
                    {arr.slice(0, 4).map((r) => (
                      <button
                        key={r.id}
                        onClick={() => { setResults(null); setQ(""); navigate(to); }}
                        className="w-full text-left px-2 py-1.5 rounded hover:bg-accent truncate"
                      >
                        {fmt(r)}
                      </button>
                    ))}
                  </div>
                ) : null
              )}
              {!results.depenses?.length && !results.fournisseurs?.length && !results.factures?.length && (
                <p className="px-2 py-2 text-muted-foreground">Aucun résultat</p>
              )}
            </div>
          )}
        </div>

        <div className="flex-1 md:hidden" />

        {/* User menu */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="gap-2 pl-2 pr-2.5" data-testid="user-menu">
              <span className="h-8 w-8 rounded-full bg-primary text-primary-foreground text-xs font-semibold flex items-center justify-center">
                {initials}
              </span>
              <span className="hidden sm:block text-left leading-tight">
                <span className="block text-sm font-medium">{user?.name}</span>
                <span className="block text-[11px] text-muted-foreground">{ROLES_LABEL[user?.role]}</span>
              </span>
              <ChevronsUpDown className="h-3.5 w-3.5 text-muted-foreground hidden sm:block" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="bg-popover w-52">
            <DropdownMenuLabel className="truncate">{user?.email}</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={logout} data-testid="logout-btn" className="text-destructive">
              <LogOut className="mr-2 h-4 w-4" /> Se déconnecter
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
